import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { KeyRound, Plus, UserCog } from 'lucide-react';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Spinner } from '@/shared/components/ui/Spinner';
import { usePortalAccountMutations, usePortalAccounts } from '../api';
import type { PortalAccount } from '../api';

function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const maybe = err as { response?: { data?: { error?: string } } };
    if (maybe.response?.data?.error) return maybe.response.data.error;
  }
  return fallback;
}

/**
 * Internal admin page: create and manage customer accounts for the Client
 * Portal (spec §1). The portal itself lives at /portal.
 */
export function PortalAccountsPage() {
  const { data: accounts = [], isLoading, isError } = usePortalAccounts();
  const { createAccount, updateAccount } = usePortalAccountMutations();

  const [createOpen, setCreateOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [clientName, setClientName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [resetTarget, setResetTarget] = useState<PortalAccount | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const submit = () => {
    if (!email.trim() || !clientName.trim() || password.length < 8) {
      toast.error('Email, client name and a password of at least 8 characters are required.');
      return;
    }
    createAccount.mutate(
      { email: email.trim(), password, clientName: clientName.trim(), contactPerson: contactPerson.trim() },
      {
        onSuccess: () => {
          toast.success('Portal account created.');
          setEmail('');
          setPassword('');
          setClientName('');
          setContactPerson('');
          setCreateOpen(false);
        },
        onError: (err) => toast.error(errorMessage(err, 'Failed to create account.')),
      },
    );
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto pb-16">
      <PageHeader
        icon={UserCog}
        eyebrow="Administration"
        title="Client Portal Accounts"
        description={<>Customer logins for the portal at <code>/portal</code>. The client name must exactly match the
              project client / invoice client name for scoped visibility.</>}
        actions={
          <><Button onClick={() => setCreateOpen(true)}>
                  <Plus className="h-4 w-4" aria-hidden />
                  New Portal Account
                </Button></>
        }
      />

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : isError ? (
        <EmptyState title="Failed to load accounts" description="Administrator access is required." />
      ) : accounts.length === 0 ? (
        <EmptyState
          title="No portal accounts yet"
          description="Create an account so a customer can sign in to the Client Portal."
          action={<Button onClick={() => setCreateOpen(true)}>New Portal Account</Button>}
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-outline">
          <table className="w-full min-w-[720px] text-left text-body-sm">
            <caption className="sr-only">Client portal accounts</caption>
            <thead className="bg-surface-variant text-on-surface-variant">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">Client</th>
                <th scope="col" className="px-3 py-2 font-medium">Email</th>
                <th scope="col" className="px-3 py-2 font-medium">Contact</th>
                <th scope="col" className="px-3 py-2 font-medium">Status</th>
                <th scope="col" className="px-3 py-2 font-medium">Last login</th>
                <th scope="col" className="px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id} className="border-t border-outline bg-surface">
                  <td className="px-3 py-2 font-medium text-on-surface">{account.clientName}</td>
                  <td className="px-3 py-2 text-on-surface-variant">{account.email}</td>
                  <td className="px-3 py-2 text-on-surface-variant">{account.contactPerson || '—'}</td>
                  <td className="px-3 py-2">
                    <Badge tone={account.active ? 'success' : 'neutral'}>{account.active ? 'Active' : 'Disabled'}</Badge>
                  </td>
                  <td className="px-3 py-2 text-on-surface-variant">
                    {account.lastLoginAt
                      ? new Date(account.lastLoginAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
                      : 'Never'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => setResetTarget(account)}>
                        <KeyRound className="h-3.5 w-3.5" aria-hidden />
                        Reset password
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={updateAccount.isPending}
                        onClick={() =>
                          updateAccount.mutate(
                            { id: account.id, active: !account.active },
                            {
                              onSuccess: () => toast.success(account.active ? 'Account disabled.' : 'Account enabled.'),
                              onError: (err) => toast.error(errorMessage(err, 'Failed to update account.')),
                            },
                          )
                        }
                      >
                        {account.active ? 'Disable' : 'Enable'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New Portal Account"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={createAccount.isPending}>
              {createAccount.isPending ? 'Creating…' : 'Create Account'}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <Input
            label="Client name (must match project/invoice client exactly)"
            name="pa-client"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
          />
          <Input label="Contact person" name="pa-contact" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} />
          <Input label="Login email" name="pa-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input
            label="Password (min 8 characters)"
            name="pa-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
      </Modal>

      {/* Reset password modal */}
      <Modal
        open={!!resetTarget}
        onClose={() => setResetTarget(null)}
        title={resetTarget ? `Reset password — ${resetTarget.clientName}` : 'Reset password'}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setResetTarget(null)}>
              Cancel
            </Button>
            <Button
              disabled={newPassword.length < 8 || updateAccount.isPending}
              onClick={() =>
                resetTarget &&
                updateAccount.mutate(
                  { id: resetTarget.id, password: newPassword },
                  {
                    onSuccess: () => {
                      toast.success('Password reset.');
                      setResetTarget(null);
                      setNewPassword('');
                    },
                    onError: (err) => toast.error(errorMessage(err, 'Failed to reset password.')),
                  },
                )
              }
            >
              Reset
            </Button>
          </div>
        }
      >
        <Input
          label="New password (min 8 characters)"
          name="pa-new-password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
      </Modal>
    </div>
  );
}
